import { createFileRoute, Link } from "@tanstack/react-router";
import { FoundingMemberBanner } from "@/components/FoundingMemberBanner";

const faq = [
  {
    q: "Was bedeutet „bei OM Selected gelistet sein“?",
    a: "Du bekommst ein eigenes, kuratiertes Profil in einem Magazin, kein offener Marktplatz und kein Bewertungssystem. Kund:innen aus dem DACH-Raum finden dich über deine Handschrift, deine Themen und deine Region.",
  },
  {
    q: "Was kostet es, als Visagist:in gelistet zu werden?",
    a: "Die Bewerbung ist kostenlos. In der Startphase ist auch die Aufnahme ins Magazin gratis. Wir nehmen keine Provision auf deine Aufträge.",
  },
  {
    q: "Wie werde ich gefunden?",
    a: "Über Stadt-Seiten wie „Visagist:in in deiner Stadt“, Themen-Seiten (z. B. Braut-Make-up, Editorial, Beauty) und über kuratierte Empfehlungen, wenn Kund:innen uns ein Brief schicken.",
  },
  {
    q: "Wie bewerbe ich mich?",
    a: "Du füllst das Bewerbungsformular aus, lädst wenige Beispielbilder hoch und schreibst ein kurzes Statement. Wir sichten jede Einreichung persönlich.",
  },
];

export const Route = createFileRoute("/fuer-visagisten")({
  head: () => ({
    meta: [
      { title: "Für Visagist:innen, gelistet werden bei OM Selected" },
      { name: "description", content: "Als Visagist:in oder Make-up Artist gelistet werden: eigenes Profil, Sichtbarkeit über Stadt- und Themen-Seiten und direkte Kundenanfragen aus DACH. Kuratiert statt Marktplatz, kostenlos." },
      { property: "og:title", content: "Für Visagist:innen, gelistet werden bei OM Selected" },
      { property: "og:description", content: "Werde als Visagist:in auf einer kuratierten Plattform gelistet, eigenes Profil, mehr Sichtbarkeit und Anfragen ohne Provision." },
      { property: "og:url", content: "https://omselected.art/fuer-visagisten" },
    ],
    links: [{ rel: "canonical", href: "https://omselected.art/fuer-visagisten" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  }),
  component: ForVisagists,
});

const benefits = [
  { t: "Eigenes Profil", d: "Eine schlanke, redaktionelle Seite mit deiner Handschrift, deinen Themen und deiner Preisrange." },
  { t: "Mehr Sichtbarkeit", d: "Du erscheinst auf Stadt- und Themen-Seiten und wirst von Kund:innen aus dem DACH-Raum gefunden." },
  { t: "Direkte Anfragen", d: "Passende Anfragen leiten wir kuratiert weiter, der Kontakt läuft direkt, ohne Provision." },
];

const steps = [
  { n: "01", t: "Bewerben", d: "Formular ausfüllen, wenige Beispielbilder und ein kurzes Statement einreichen." },
  { n: "02", t: "Kuratierung", d: "Wir sichten jede Einreichung persönlich nach Handschrift und Haltung." },
  { n: "03", t: "Gelistet werden", d: "Dein Profil geht live, du pflegst Galerie, Themen und Preise selbst." },
];

function ForVisagists() {
  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-12 pb-16">
          <div className="eyebrow text-muted-foreground">Für Visagist:innen</div>
          <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">Werde als Visagist:in gelistet</h1>
          <p className="mt-6 max-w-xl text-muted-foreground text-lg leading-relaxed">
            OM Selected ist eine kuratierte Plattform, kein Marktplatz. Mit deinem eigenen Profil wirst du über Stadt- und Themen-Seiten gefunden und bekommst direkte Anfragen aus dem DACH-Raum.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 eyebrow">
            <Link to="/apply" search={{ as: "visagist" }} className="border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors">Jetzt bewerben</Link>
            <Link to="/visagists" className="border border-border px-5 py-3 hover:border-foreground transition-colors">Visagist:innen ansehen</Link>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-16 md:py-20 grid md:grid-cols-3 gap-10">
          {benefits.map((b) => (
            <div key={b.t}>
              <h2 className="serif text-2xl leading-tight">{b.t}</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-16 md:py-20">
          <div className="eyebrow text-muted-foreground">So funktioniert es</div>
          <div className="mt-8 grid md:grid-cols-3 gap-10">
            {steps.map((s) => (
              <div key={s.n}>
                <div className="serif text-4xl text-muted-foreground">{s.n}</div>
                <h3 className="serif text-2xl mt-3 leading-tight">{s.t}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FoundingMemberBanner kind="visagist" />

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow text-muted-foreground">Häufige Fragen</div>
            <h2 className="serif text-3xl md:text-4xl mt-3 leading-tight">Gut zu wissen</h2>
          </div>
          <div className="md:col-span-8 divide-y divide-border">
            {faq.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex justify-between items-start gap-6 cursor-pointer list-none">
                  <h3 className="serif text-xl leading-snug">{item.q}</h3>
                  <span className="eyebrow text-muted-foreground shrink-0 mt-1 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-20 text-center">
          <blockquote className="serif italic text-3xl md:text-4xl leading-snug">Weniger zeigen, länger angesehen werden.</blockquote>
          <div className="mt-12 flex flex-wrap justify-center gap-3 eyebrow">
            <Link to="/apply" search={{ as: "visagist" }} className="border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors">Als Visagist:in bewerben</Link>
            <Link to="/fuer-fotografen" className="border border-border px-5 py-3 hover:border-foreground transition-colors">Für Fotograf:innen</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
